import { useEffect, useState, useRef } from 'react';
import { speakersAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ImageUpload } from '../../components/ui/image-upload';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, Home, Upload, FileJson, CheckCircle, XCircle, AlertCircle, Eye } from 'lucide-react';
import api from '../../lib/api';

export const SpeakersAdminPage = () => {
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSpeaker, setEditingSpeaker] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    image: '',
    company: '',
    title: '',
    bio: '',
    linkedin: '',
    slug: '',
    show_on_homepage: false,
    is_public: false,
  });

  useEffect(() => {
    loadSpeakers();
  }, []);

  const loadSpeakers = async () => {
    try {
      const res = await speakersAPI.getAll();
      setSpeakers(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      image: '',
      company: '',
      title: '',
      bio: '',
      linkedin: '',
      slug: '',
      show_on_homepage: false,
      is_public: false,
    });
    setEditingSpeaker(null);
  };

  const generateSlug = (firstName, lastName) => {
    return `${firstName}-${lastName}`.toLowerCase()
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const openEditDialog = (speaker) => {
    setEditingSpeaker(speaker);
    setFormData({
      first_name: speaker.first_name,
      last_name: speaker.last_name,
      image: speaker.image || '',
      company: speaker.company || '',
      title: speaker.title || '',
      bio: speaker.bio || '',
      linkedin: speaker.linkedin || '',
      slug: speaker.slug,
      show_on_homepage: speaker.show_on_homepage || false,
      is_public: speaker.is_public || false,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        slug: formData.slug || generateSlug(formData.first_name, formData.last_name),
      };

      if (editingSpeaker) {
        await speakersAPI.update(editingSpeaker.id, data);
        toast.success('Speaker aggiornato');
      } else {
        await speakersAPI.create(data);
        toast.success('Speaker creato');
      }
      setDialogOpen(false);
      resetForm();
      loadSpeakers();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Sei sicuro di voler eliminare questo speaker?')) return;
    try {
      await speakersAPI.delete(id);
      toast.success('Speaker eliminato');
      loadSpeakers();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const togglePublic = async (speaker) => {
    try {
      await speakersAPI.update(speaker.id, { is_public: !speaker.is_public });
      setSpeakers(prev => prev.map(s => s.id === speaker.id ? { ...s, is_public: !s.is_public } : s));
    } catch {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setImporting(true);
      setImportResult(null);

      const text = await file.text();
      const json = JSON.parse(text);

      let payload;
      if (json.items && Array.isArray(json.items)) {
        payload = json;
      } else if (Array.isArray(json)) {
        payload = { speakers: json };
      } else if (json.speakers) {
        payload = json;
      } else {
        toast.error('Formato file non riconosciuto');
        return;
      }

      // Start async import
      const startRes = await api.post('/speakers/import', payload);
      const jobId = startRes.data.job_id;
      const total = startRes.data.total;
      toast.info(`Import avviato: ${total} speaker in elaborazione...`);

      // Poll for progress
      const poll = async () => {
        while (true) {
          await new Promise(r => setTimeout(r, 2000));
          try {
            const statusRes = await api.get(`/speakers/import/${jobId}`);
            const job = statusRes.data;
            setImportResult({
              created: job.created,
              updated: job.updated,
              errors: job.errors,
              details: job.details,
              _progress: `${job.processed}/${job.total}`,
            });

            if (job.status === 'completed') {
              if (job.created > 0) toast.success(`${job.created} speaker creati`);
              if (job.updated > 0) toast.info(`${job.updated} speaker aggiornati`);
              if (job.errors > 0) toast.error(`${job.errors} errori durante l'import`);
              loadSpeakers();
              break;
            }
          } catch {
            break;
          }
        }
      };
      await poll();

    } catch (error) {
      console.error('Import error:', error);
      toast.error('Errore durante l\'importazione: ' + (error.message || 'File non valido'));
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-6" data-testid="speakers-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">Speakers</h1>
          <p className="text-muted-foreground">Gestisci gli speakers SIOS</p>
        </div>

        <div className="flex items-center gap-3">
          {/* Import Button */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImportFile}
            accept=".json"
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            data-testid="import-speakers-btn"
          >
            {importing ? (
              <>
                <FileJson size={18} className="mr-2 animate-pulse" />
                {importResult?._progress ? `Importazione ${importResult._progress}...` : 'Importazione...'}
              </>
            ) : (
              <>
                <Upload size={18} className="mr-2" />
                Importa JSON
              </>
            )}
          </Button>

          {/* Import Result Dialog */}
          <Dialog open={importResult !== null} onOpenChange={(open) => { 
            if (!open) setImportResult(null);
          }}>
            <DialogContent className="bg-card border-white/10 max-w-lg max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <FileJson size={20} />
                  Risultato Importazione
                </DialogTitle>
              </DialogHeader>
              {importResult && (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg bg-green-500/20 border border-green-500/30 text-center">
                      <p className="text-2xl font-bold text-green-500">{importResult.created}</p>
                      <p className="text-xs text-green-400">Creati</p>
                    </div>
                    <div className="p-4 rounded-lg bg-blue-500/20 border border-blue-500/30 text-center">
                      <p className="text-2xl font-bold text-blue-500">{importResult.updated}</p>
                      <p className="text-xs text-blue-400">Aggiornati</p>
                    </div>
                    <div className="p-4 rounded-lg bg-red-500/20 border border-red-500/30 text-center">
                      <p className="text-2xl font-bold text-red-500">{importResult.errors}</p>
                      <p className="text-xs text-red-400">Errori</p>
                    </div>
                  </div>

                  {/* Details */}
                  {importResult.details && importResult.details.length > 0 && (
                    <div className="space-y-2">
                      <Label>Dettagli ({importResult.details.length} speaker)</Label>
                      <div className="max-h-64 overflow-y-auto space-y-1 p-2 rounded-lg bg-white/5">
                        {importResult.details.map((detail, idx) => (
                          <div 
                            key={`import-${detail.name}-${idx}`}
                            className={`flex items-center justify-between p-2 rounded text-sm ${
                              detail.status === 'created' 
                                ? 'bg-green-500/10 text-green-400'
                                : detail.status === 'updated'
                                ? 'bg-blue-500/10 text-blue-400'
                                : 'bg-red-500/10 text-red-400'
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              {detail.status === 'created' && <CheckCircle size={14} />}
                              {detail.status === 'updated' && <AlertCircle size={14} />}
                              {detail.status === 'error' && <XCircle size={14} />}
                              {detail.name}
                            </span>
                            <span className="text-xs opacity-70">
                              {detail.reason || detail.image}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button 
                    onClick={() => setImportResult(null)} 
                    className="w-full"
                  >
                    Chiudi
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Create Speaker Dialog */}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="create-speaker-btn">
                <Plus size={18} className="mr-2" />
                Nuovo Speaker
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingSpeaker ? 'Modifica Speaker' : 'Nuovo Speaker'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="Mario"
                    className="bg-white/5 border-white/10"
                    required
                    data-testid="input-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cognome *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Rossi"
                    className="bg-white/5 border-white/10"
                    required
                    data-testid="input-last-name"
                  />
                </div>
              </div>

              <ImageUpload
                value={formData.image}
                onChange={(url) => setFormData({ ...formData, image: url })}
                label="Foto Speaker"
              />

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Azienda</Label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="TechCorp"
                    className="bg-white/5 border-white/10"
                    data-testid="input-company"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ruolo</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="CEO"
                    className="bg-white/5 border-white/10"
                    data-testid="input-title"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Bio</Label>
                <Textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Biografia dello speaker..."
                  className="bg-white/5 border-white/10 min-h-24"
                  data-testid="input-bio"
                />
              </div>

              <div className="space-y-2">
                <Label>LinkedIn</Label>
                <Input
                  value={formData.linkedin}
                  onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                  placeholder="https://linkedin.com/in/..."
                  className="bg-white/5 border-white/10"
                  data-testid="input-linkedin"
                />
              </div>

              <div className="space-y-2">
                <Label>Slug</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="mario-rossi"
                  className="bg-white/5 border-white/10"
                  data-testid="input-slug"
                />
                <p className="text-xs text-muted-foreground">Lascia vuoto per generarlo automaticamente</p>
              </div>

              {/* Show on Homepage Toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-primary/10 border border-primary/20">
                <div className="flex items-center gap-3">
                  <Home className="text-primary" size={20} />
                  <div>
                    <Label className="text-base cursor-pointer">Mostra in Homepage</Label>
                    <p className="text-sm text-muted-foreground">
                      Lo speaker apparirà nella sezione speakers della homepage
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.show_on_homepage}
                    onChange={(e) => setFormData({ ...formData, show_on_homepage: e.target.checked })}
                    className="sr-only peer"
                    data-testid="toggle-show-on-homepage"
                  />
                  <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              {/* Public Toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                <div className="flex items-center gap-3">
                  <Eye className="text-green-400" size={20} />
                  <div>
                    <Label className="text-base cursor-pointer">Visibile nella pagina Speaker</Label>
                    <p className="text-sm text-muted-foreground">
                      Lo speaker apparirà nella pagina pubblica Speaker
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_public}
                    onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                    className="sr-only peer"
                    data-testid="toggle-is-public"
                  />
                  <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Annulla
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="submit-speaker-btn">
                  {editingSpeaker ? 'Aggiorna' : 'Crea'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Table */}
      <div className="glass rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead>Speaker</TableHead>
              <TableHead>Azienda</TableHead>
              <TableHead>Ruolo</TableHead>
              <TableHead>Pubblico</TableHead>
              <TableHead>Homepage</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {speakers.map((speaker) => {
              const imageUrl = speaker.image 
                ? (speaker.image.startsWith('http') ? speaker.image : `${process.env.REACT_APP_BACKEND_URL}${speaker.image}`)
                : `https://ui-avatars.com/api/?name=${speaker.first_name}+${speaker.last_name}&background=7B2CBF&color=fff&size=40`;
              
              return (
                <TableRow key={speaker.id} className="border-white/5" data-testid={`speaker-row-${speaker.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <img 
                        src={imageUrl}
                        alt={`${speaker.first_name} ${speaker.last_name}`}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <span className="font-medium">{speaker.first_name} {speaker.last_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>{speaker.company || '-'}</TableCell>
                  <TableCell>{speaker.title || '-'}</TableCell>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => togglePublic(speaker)}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                        speaker.is_public
                          ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                          : 'bg-white/5 text-muted-foreground hover:bg-white/10'
                      }`}
                      data-testid={`toggle-public-${speaker.id}`}
                    >
                      {speaker.is_public ? 'Visibile' : 'Nascosto'}
                    </button>
                  </TableCell>
                  <TableCell>
                    {speaker.show_on_homepage ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/20 text-primary text-xs font-medium">
                        <Home size={12} />
                        Visibile
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openEditDialog(speaker)}
                        data-testid={`edit-speaker-${speaker.id}`}
                      >
                        <Pencil size={16} />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(speaker.id)}
                      data-testid={`delete-speaker-${speaker.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
              );
            })}

            {speakers.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12">
                  <Users className="mx-auto text-muted-foreground mb-4" size={32} />
                  <p className="text-muted-foreground">Nessuno speaker trovato</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
